import { z } from 'zod';
import { defineFeature } from '../core/defineFeature.js';
import { FeatureType } from '../core/types.js';
const ChatFunctionality = { id: 1, name: 'Chat Functionality' };
export const voiceMode = defineFeature({
    type: FeatureType.Boolean,
    propName: 'voice_mode',
    uiName: 'voiceMode',
    displayName: 'Voice Mode',
    description: 'Enable voice interaction mode',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatLiveAvatar = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_live_avatar',
    uiName: 'chatLiveAvatar',
    displayName: 'Chat Live Avatar',
    description: 'Enable HeyGen live avatar video experience',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatInteractionAvailableModes = defineFeature({
    type: FeatureType.MultiSelect,
    propName: 'chat_interaction_available_modes',
    uiName: 'chatInteractionAvailableModes',
    displayName: 'Chat Interaction Available Modes',
    description: 'Available chat interaction modes a user can choose from',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.array(z.string()),
    default: ['text'],
    validValues: ['text', 'voice', 'video'],
    options: {
        text: { displayName: 'Text', required: true },
        voice: { displayName: 'Voice', prerequisite: 'voice_mode' },
        video: { displayName: 'Video', prerequisite: 'chat_live_avatar' },
    },
    isSystem: true,
});
export const chatInteractionDefaultMode = defineFeature({
    type: FeatureType.String,
    propName: 'chat_interaction_default_mode',
    uiName: 'chatInteractionDefaultMode',
    displayName: 'Chat Interaction Default Mode',
    description: 'Default chat interaction mode for user on load',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: 'text',
    validValues: ['text', 'voice', 'video'],
    validValuesFrom: 'chat_interaction_available_modes',
    isSystem: true,
});
export const uploadFiles = defineFeature({
    type: FeatureType.Boolean,
    propName: 'upload_files',
    uiName: 'uploadFiles',
    displayName: 'Upload Files',
    description: 'Enable file uploads in chat',
    category: ChatFunctionality,
    areas: ['application'],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const copyChatMessages = defineFeature({
    type: FeatureType.Boolean,
    propName: 'copy_chat_messages',
    uiName: 'copyChatMessages',
    displayName: 'Copy Chat Messages',
    description: 'Allow users to copy chat messages',
    category: ChatFunctionality,
    areas: ['application'],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const shareChats = defineFeature({
    type: FeatureType.Boolean,
    propName: 'share_chats',
    uiName: 'shareChats',
    displayName: 'Share Chats',
    description: 'Allow users to share chat conversations',
    category: ChatFunctionality,
    areas: ['application'],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const exportChatData = defineFeature({
    type: FeatureType.Boolean,
    propName: 'export_chat_data',
    uiName: 'exportChatData',
    displayName: 'Export Chat Data',
    description: 'Allow users to export chat data',
    category: ChatFunctionality,
    areas: ['application'],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const deleteChatsOnSessionEnd = defineFeature({
    type: FeatureType.Boolean,
    propName: 'delete_chats_on_session_end',
    uiName: 'deleteChatsOnSessionEnd',
    displayName: 'Delete Chats on Session End',
    description: 'Automatically delete chat history when session ends',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatReadAloud = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_read_aloud',
    uiName: 'chatReadAloud',
    displayName: 'Chat Read Aloud',
    description: 'Enable text-to-speech for chat messages',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatVoiceInput = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_voice_input',
    uiName: 'chatVoiceInput',
    displayName: 'Chat Voice Input',
    description: 'Enable voice input for chat',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatWelcomeScreen = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_welcome_screen',
    uiName: 'chatWelcomeScreen',
    displayName: 'Chat Welcome Screen',
    description: 'Display welcome screen on chat initialization',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatWelcomeScreenContent = defineFeature({
    type: FeatureType.Markdown,
    propName: 'chat_welcome_screen_content',
    uiName: 'chatWelcomeScreenContent',
    displayName: 'Chat Welcome Screen Content',
    description: 'Customize the welcome screen title and subtitle using Markdown',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: '',
    isSystem: true,
    flatModelSchema: z.string().nullish(),
});
/**
 * Legacy DB default is null for this Number feature; the registry
 * normalizes to 0 since NumberDef requires a numeric default. Consumers
 * treating 0 as "no expiration" should continue to work.
 */
export const chatExpirationHours = defineFeature({
    type: FeatureType.Number,
    propName: 'chat_expiration_hours',
    uiName: 'chatExpirationHours',
    displayName: 'Chat Expiration (Hours)',
    description: 'Number of hours after which chat expires',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.number(),
    default: 0,
    isSystem: true,
    flatModelSchema: z.number().nullable(),
});
export const chatGuidelines = defineFeature({
    type: FeatureType.Markdown,
    propName: 'chat_guidelines',
    uiName: 'chatGuidelines',
    displayName: 'Chat Guidelines',
    description: 'Guidelines to display to users in chat',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: '',
    isSystem: true,
    flatModelSchema: z.string().nullable(),
});
export const speechServicesProvider = defineFeature({
    type: FeatureType.String,
    propName: 'speech_services_provider',
    uiName: 'speechServicesProvider',
    displayName: 'Speech Services Provider',
    description: 'Speech-to-text service provider',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: 'browser',
    validValues: ['browser', 'azure-speech-services', 'aws-speech-services', 'custom'],
    isSystem: true,
    flatModelSchema: z.enum(['browser', 'azure-speech-services', 'aws-speech-services', 'custom']),
});
export const chatLiveAvatarId = defineFeature({
    type: FeatureType.String,
    propName: 'chat_live_avatar_id',
    uiName: 'chatLiveAvatarId',
    displayName: 'Chat Live Avatar Appearance',
    description: 'Select which HeyGen avatar appearance to use for live avatar',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: '7b888024-f8c9-4205-95e1-78ce01497bda',
    validValues: [
        '7b888024-f8c9-4205-95e1-78ce01497bda',
        '567e8371-f69f-49ec-9f2d-054083431165',
        '5761a14c-8720-4ce1-8c2b-3f351718fc79',
        '0f563214-1cb5-4dc0-a2f9-43f44e5e6b57',
        '40b4f000-f783-4bba-a327-ea58b1a6fdf2',
        '91342979-4c4c-44f1-bd3b-1c846d20341e',
        '5dd4d830-957a-419f-9334-0dc4399ada5d',
        '3f291b22-0267-4fb6-a25b-847fb63604b0',
        'bf00036b-558a-44b5-b2ff-1e3cec0f4ceb',
    ],
    isSystem: true,
    editor: 'avatar-select',
});
/**
 * Legacy column stores a JSON-encoded object as a string. The registry
 * keeps the type as String + codec 'identity' since the runtime value IS
 * the encoded string; downstream consumers that need the parsed shape do
 * so explicitly at the boundary.
 */
export const chatLiveAvatarTtsConfig = defineFeature({
    type: FeatureType.String,
    propName: 'chat_live_avatar_tts_config',
    uiName: 'chatLiveAvatarTtsConfig',
    displayName: 'Chat Live Avatar TTS Config',
    description: 'Configure text-to-speech provider and voice for the chat live avatar',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: '{"type":"polly","voiceId":"Joanna","engine":"neural"}',
    isSystem: true,
    editor: 'tts-config',
    flatModelSchema: z.preprocess((val) => (typeof val === 'string' ? JSON.parse(val) : val), z.object({
        type: z.enum(['polly']),
        voiceId: z.string(),
        engine: z.enum(['standard', 'neural', 'long-form', 'generative']).default('neural'),
    })),
});
export const chatReadAloudTtsConfig = defineFeature({
    type: FeatureType.String,
    propName: 'chat_read_aloud_tts_config',
    uiName: 'chatReadAloudTtsConfig',
    displayName: 'Chat Read Aloud TTS Config',
    description: 'Configure text-to-speech provider and voice for the Read Aloud feature',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.string(),
    default: '{"type":"browser"}',
    isSystem: true,
    editor: 'tts-config',
    flatModelSchema: z.preprocess((val) => (typeof val === 'string' ? JSON.parse(val) : val), z.discriminatedUnion('type', [
        z.object({
            type: z.literal('polly'),
            voiceId: z.string(),
            engine: z.enum(['standard', 'neural', 'long-form', 'generative']).default('neural'),
        }),
        z.object({
            type: z.literal('openai'),
        }),
        z.object({
            type: z.literal('browser'),
        }),
    ])),
});
export const followUps = defineFeature({
    type: FeatureType.Boolean,
    propName: 'follow_ups',
    uiName: 'followUps',
    displayName: 'Follow Ups',
    description: 'Enable/Disable follow up questions in the chat',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatAuraIframesEnabled = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_aura_iframes_enabled',
    uiName: 'chatAuraIframesEnabled',
    displayName: 'Chat Aura Iframe',
    description: 'Enable/Disable Aura iframes integration in the chat interface',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
    flatModelSchema: z.boolean().optional(),
});
export const chatFileUploads = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_file_uploads',
    uiName: 'chatFileUploads',
    displayName: 'Chat File Uploads',
    description: 'Allow users to upload files in chat',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const complianceLayer = defineFeature({
    type: FeatureType.Boolean,
    propName: 'compliance_layer',
    uiName: 'complianceLayer',
    displayName: 'Compliance Layer',
    description: 'Enable/Disable compliance layer for content screening',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
/**
 * Legacy DB default was empty string and coerced to 0; normalized here to
 * a numeric 0. chat-api treats 0 as the "use the 5000 fallback" sentinel
 * (see apps/chat-api/src/middleware/rate-limit.ts) and null as "no limit".
 * The registry default cannot be null because NumberDef requires a number,
 * so an explicit override is the only way to set unlimited usage.
 */
export const maxUserChatQueries = defineFeature({
    type: FeatureType.Number,
    propName: 'max_user_chat_queries',
    uiName: 'maxUserChatQueries',
    displayName: 'Max User Chat Queries',
    description: 'Rate limit for query requests per user per year. 0 falls back to the chat-api default (5000); explicit null means no limit.',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.number(),
    default: 0,
    isSystem: true,
    flatModelSchema: z.number().nullable(),
});
export const citationsEnabled = defineFeature({
    type: FeatureType.Boolean,
    propName: 'citations_enabled',
    uiName: 'citationsEnabled',
    displayName: 'Citations Enabled',
    description: 'Enable citation rendering in chat responses',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const disableChatInputWithLandingCards = defineFeature({
    type: FeatureType.Boolean,
    propName: 'disable_chat_input_with_landing_cards',
    uiName: 'disableChatInputWithLandingCards',
    displayName: 'Disable Chat Input With Landing Cards',
    description: 'When enabled, disables the chat input box when landing cards are shown',
    category: ChatFunctionality,
    areas: [],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatAtMentionFilter = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_at_mention_filter',
    uiName: 'chatAtMentionFilter',
    displayName: '@ Mention Subject Filter (Chat Input)',
    description: 'Enables @ mention subject autocomplete in the chat message input',
    category: ChatFunctionality,
    areas: ['brand'],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: false,
});
export const chatInputAttachmentsEnabled = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_input_attachments_enabled',
    uiName: 'chatInputAttachmentsEnabled',
    displayName: 'Chat Input Attachments',
    description: 'Allow users to attach files directly in the chat input and send signed URLs to agents',
    category: ChatFunctionality,
    areas: ['brand'],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const chatInputAttachmentAllowedMimeTypes = defineFeature({
    type: FeatureType.MultiSelect,
    propName: 'chat_input_attachment_allowed_mime_types',
    uiName: 'chatInputAttachmentAllowedMimeTypes',
    displayName: 'Chat Input Attachments Allowed File Types',
    description: 'MIME types accepted for chat input attachments. Empty means all supported types are allowed.',
    category: ChatFunctionality,
    areas: ['brand'],
    codec: 'identity',
    schema: z.array(z.string()),
    default: [],
    validValues: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // 'audio/mpeg',
        // 'video/mp4',
        'text/csv',
        'image/jpeg',
        'image/png',
    ],
    options: {
        'application/pdf': { displayName: 'PDF' },
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
            displayName: 'Word Document (.docx)',
        },
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
            displayName: 'PowerPoint (.pptx)',
        },
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
            displayName: 'Excel Spreadsheet (.xlsx)',
        },
        // 'audio/mpeg': { displayName: 'MP3 Audio' },
        // 'video/mp4': { displayName: 'MP4 Video' },
        'text/csv': { displayName: 'CSV' },
        'image/jpeg': { displayName: 'JPEG Image (.jpg, .jpeg)' },
        'image/png': { displayName: 'PNG Image' },
    },
    requiresFeature: ['chat_input_attachments_enabled'],
    isSystem: true,
});
export const chatInputAttachmentCustomS3Enabled = defineFeature({
    type: FeatureType.Boolean,
    propName: 'chat_input_attachment_custom_s3_enabled',
    uiName: 'chatInputAttachmentCustomS3Enabled',
    displayName: 'Custom S3 Bucket',
    description: 'Use a custom S3 bucket for chat input attachment storage instead of the server default.',
    category: ChatFunctionality,
    areas: ['brand'],
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
    requiresFeature: ['chat_input_attachments_enabled'],
});
export const chatInputAttachmentS3Config = defineFeature({
    type: FeatureType.Json,
    propName: 'chat_input_attachment_s3_config',
    uiName: 'chatInputAttachmentS3Config',
    displayName: 'S3 Data Connection',
    description: 'Identifier of the S3 Bucket data connection to use for chat input attachment storage.',
    category: ChatFunctionality,
    areas: ['brand'],
    codec: 'json-string',
    schema: z.object({
        dataConnectionIdentifier: z.string(),
    }),
    default: {
        dataConnectionIdentifier: '',
    },
    isSystem: true,
    editor: 's3-upload-config',
    requiresFeature: ['chat_input_attachment_custom_s3_enabled'],
});
export const patientJourneyJourneys = defineFeature({
    type: FeatureType.Boolean,
    propName: 'patient_journey_journeys',
    uiName: 'patientJourneyJourneys',
    displayName: 'Patient Journey: Multi-Journey',
    description: 'Enable multi-journey patient journey navigation and contracts',
    category: ChatFunctionality,
    areas: ['application'],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const chatFunctionalityFeatures = [
    voiceMode,
    chatLiveAvatar,
    chatInteractionAvailableModes,
    chatInteractionDefaultMode,
    uploadFiles,
    copyChatMessages,
    shareChats,
    exportChatData,
    deleteChatsOnSessionEnd,
    chatReadAloud,
    chatVoiceInput,
    chatWelcomeScreen,
    chatWelcomeScreenContent,
    chatExpirationHours,
    chatGuidelines,
    speechServicesProvider,
    chatLiveAvatarId,
    chatLiveAvatarTtsConfig,
    chatReadAloudTtsConfig,
    followUps,
    chatAuraIframesEnabled,
    chatFileUploads,
    complianceLayer,
    maxUserChatQueries,
    citationsEnabled,
    disableChatInputWithLandingCards,
    chatAtMentionFilter,
    chatInputAttachmentsEnabled,
    chatInputAttachmentAllowedMimeTypes,
    chatInputAttachmentCustomS3Enabled,
    chatInputAttachmentS3Config,
    patientJourneyJourneys,
];
